import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fetchHealthSummaryData } from '../services/healthSummaryService';
import type { HealthSummaryData } from '../types/healthSummary';

const DashboardHome: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [healthData, setHealthData] = useState<HealthSummaryData | null>(null);

  const today = new Date().toLocaleDateString('en-CA', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });

  useEffect(() => {
    if (!user?.username) return;
    fetchHealthSummaryData(user.username)
      .then((result) => { if (result) setHealthData(result.data); })
      .catch(() => {});
  }, [user?.username]);

  const emergencyContact = healthData?.emergencyContacts?.[0];
  const conditions = healthData?.conditions || [];
  const allergies = healthData?.allergies || [];
  const medications = healthData?.medications || [];
  const familyDoctor = healthData?.familyDoctor?.find(
    (d) => d.role.toLowerCase().includes('family') || d.role.toLowerCase().includes('doctor')
  );
  const pharmacy = healthData?.familyDoctor?.find(
    (d) => d.role.toLowerCase().includes('pharmacy')
  );

  return (
    <div className="dashboard-home">
      {/* Welcome Banner */}
      <div className="dh-welcome">
        <h2>Welcome back{user?.firstName ? `, ${user.firstName}` : ''}</h2>
        <div className="dh-welcome-meta">
          <div className="dh-meta-item">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--cream)" strokeWidth="2" style={{ opacity: 0.7 }}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            <span style={{ color: 'var(--cream)' }}>{today}</span>
          </div>
        </div>
      </div>

      {/* Action Cards */}
      <div className="dh-actions">
        <div className="dh-action-card" onClick={() => navigate('/dashboard/health-summary')}>
          <div className="dh-ac-icon" style={{ background: 'rgba(80,0,108,.08)', color: 'var(--primary)' }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
          </div>
          <div>
            <div className="dh-ac-title">My Health Summary</div>
            <div className="dh-ac-desc">Keep your health history at your fingertips.</div>
          </div>
          <div className="dh-ac-link">Go to <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg></div>
        </div>

        <div className="dh-action-card" onClick={() => navigate('/dashboard/emergency-code')}>
          <div className="dh-ac-icon" style={{ background: 'rgba(206,140,165,.12)', color: 'var(--pink)' }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          </div>
          <div>
            <div className="dh-ac-title">My Emergency Card</div>
            <div className="dh-ac-desc">Carry your critical health info for when it matters most.</div>
          </div>
          <div className="dh-ac-link">Go to <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg></div>
        </div>

        <div className="dh-action-card" onClick={() => navigate('/dashboard/profile')}>
          <div className="dh-ac-icon" style={{ background: 'rgba(80,0,108,.06)', color: 'var(--primary-light)' }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          </div>
          <div>
            <div className="dh-ac-title">My Account</div>
            <div className="dh-ac-desc">Manage your account details and preferences.</div>
          </div>
          <div className="dh-ac-link">Go to <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg></div>
        </div>
      </div>

      {/* At a Glance + Care Team */}
      {healthData && (
        <div className="dh-panels">
          {/* At a Glance */}
          <div className="dh-panel">
            <div className="dh-panel-title">At a Glance</div>
            <div className="dh-panel-card">
              {emergencyContact && (
                <div className="dh-glance-row dh-glance-emergency">
                  <div className="dh-glance-icon" style={{ background: 'var(--red-bg)' }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--red)" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.87.36 1.72.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c1.09.34 1.94.57 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                  </div>
                  <div className="dh-glance-info">
                    <div className="dh-glance-label">Emergency Contact</div>
                    <div className="dh-glance-value">{emergencyContact.name}{emergencyContact.relationship ? ` (${emergencyContact.relationship})` : ''}</div>
                    {emergencyContact.phone && <div className="dh-glance-phone">{emergencyContact.phone}</div>}
                  </div>
                </div>
              )}

              {conditions.length > 0 && (
                <div className="dh-glance-row dh-glance-clickable" onClick={() => navigate('/dashboard/health-summary')}>
                  <div className="dh-glance-icon" style={{ background: 'var(--primary-bg)' }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
                  </div>
                  <div className="dh-glance-info">
                    <div className="dh-glance-label">{conditions.length} Medical Condition{conditions.length !== 1 ? 's' : ''}</div>
                    <div className="dh-glance-sub">{conditions.map((c) => c.condition).join(', ')}</div>
                  </div>
                </div>
              )}

              {allergies.length > 0 && (
                <div className="dh-glance-row dh-glance-clickable" onClick={() => navigate('/dashboard/health-summary')}>
                  <div className="dh-glance-icon" style={{ background: 'var(--amber-bg)' }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--amber)" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                  </div>
                  <div className="dh-glance-info">
                    <div className="dh-glance-label">{allergies.length} Allerg{allergies.length !== 1 ? 'ies' : 'y'}</div>
                    <div className="dh-glance-sub dh-glance-amber">
                      {allergies.map((a) => `${a.allergen}${a.severity ? ` (${a.severity})` : ''}`).join(' \u00B7 ')}
                    </div>
                  </div>
                </div>
              )}

              {medications.length > 0 && (
                <div className="dh-glance-row dh-glance-clickable" onClick={() => navigate('/dashboard/health-summary')}>
                  <div className="dh-glance-icon" style={{ background: 'var(--pink-bg)' }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--pink)" strokeWidth="2"><circle cx="8" cy="18" r="3"/><circle cx="16" cy="18" r="3"/><path d="M11 18h-0.5a4 4 0 0 1-4-4V8a4 4 0 0 1 4-4h3a4 4 0 0 1 4 4v6a4 4 0 0 1-4 4H13"/></svg>
                  </div>
                  <div className="dh-glance-info">
                    <div className="dh-glance-label">{medications.length} Medication{medications.length !== 1 ? 's' : ''}</div>
                    <div className="dh-glance-sub">{medications.map((m) => m.name).join(', ')}</div>
                  </div>
                </div>
              )}

              <div className="dh-panel-footer">
                <button className="dh-panel-link" onClick={() => navigate('/dashboard/health-summary')}>View full Health Summary &rarr;</button>
              </div>
            </div>
          </div>

          {/* My Care Team */}
          <div className="dh-panel">
            <div className="dh-panel-title">My Care Team</div>
            <div className="dh-panel-card">
              {familyDoctor && (
                <div className="dh-care-row">
                  <div className="dh-care-avatar" style={{ background: 'var(--primary-bg)' }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                  </div>
                  <div className="dh-care-info">
                    <div className="dh-care-role">Family Doctor</div>
                    <div className="dh-care-name">{familyDoctor.name}</div>
                    {familyDoctor.address && <div className="dh-care-detail">{familyDoctor.address}</div>}
                    <div className="dh-care-contacts">
                      {familyDoctor.phone && <div className="dh-care-contact"><span className="dh-care-contact-label">Tel:</span> <span className="dh-care-contact-value">{familyDoctor.phone}</span></div>}
                      {familyDoctor.fax && <div className="dh-care-contact"><span className="dh-care-contact-label">Fax:</span> <span className="dh-care-contact-fax">{familyDoctor.fax}</span></div>}
                    </div>
                  </div>
                </div>
              )}

              {pharmacy && (
                <div className="dh-care-row">
                  <div className="dh-care-avatar" style={{ background: 'var(--amber-bg)' }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--amber)" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                  </div>
                  <div className="dh-care-info">
                    <div className="dh-care-role">Preferred Pharmacy</div>
                    <div className="dh-care-name">{pharmacy.name}</div>
                    {pharmacy.address && <div className="dh-care-detail">{pharmacy.address}</div>}
                    <div className="dh-care-contacts">
                      {pharmacy.phone && <div className="dh-care-contact"><span className="dh-care-contact-label">Tel:</span> <span className="dh-care-contact-value">{pharmacy.phone}</span></div>}
                      {pharmacy.fax && <div className="dh-care-contact"><span className="dh-care-contact-label">Fax:</span> <span className="dh-care-contact-fax">{pharmacy.fax}</span></div>}
                    </div>
                  </div>
                </div>
              )}

              <div className="dh-panel-footer">
                <button className="dh-panel-link" onClick={() => navigate('/dashboard/health-summary')}>View full care team in My Health Summary &rarr;</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .dh-welcome {
          background: linear-gradient(135deg, rgb(80, 0, 108), rgb(110, 20, 145) 50%, rgb(130, 40, 165));
          border-radius: 20px;
          padding: 26px 36px;
          position: relative;
          overflow: hidden;
          margin-bottom: 28px;
        }
        .dh-welcome::before {
          content: '';
          position: absolute;
          top: -40px;
          right: -40px;
          width: 200px;
          height: 200px;
          border-radius: 50%;
          background: rgba(206, 140, 165, .15);
        }
        .dh-welcome::after {
          content: '';
          position: absolute;
          bottom: -50px;
          right: 80px;
          width: 140px;
          height: 140px;
          border-radius: 50%;
          background: rgba(245, 232, 218, .08);
        }
        .dh-welcome h2 {
          font-family: var(--font-display);
          font-size: 26px;
          color: var(--cream);
          margin-bottom: 8px;
          position: relative;
          z-index: 1;
        }
        .dh-welcome-meta {
          display: flex;
          gap: 24px;
          flex-wrap: wrap;
          position: relative;
          z-index: 1;
        }
        .dh-meta-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 16px;
        }

        /* Action Cards */
        .dh-actions {
          display: flex;
          gap: 20px;
          margin-bottom: 32px;
        }
        .dh-action-card {
          flex: 1;
          background: var(--white);
          border: 1.5px solid var(--border);
          border-radius: var(--radius);
          padding: 28px 24px;
          cursor: pointer;
          text-align: left;
          transition: all 0.2s ease;
          display: flex;
          flex-direction: column;
          gap: 14px;
          box-shadow: var(--shadow-sm);
        }
        .dh-action-card:hover {
          transform: translateY(-3px);
          box-shadow: var(--shadow-md);
          border-color: var(--primary);
        }
        .dh-ac-icon {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .dh-ac-title {
          font-family: var(--font-display);
          font-weight: 700;
          font-size: 19px;
          color: var(--text);
          margin-bottom: 4px;
        }
        .dh-ac-desc {
          font-size: 15px;
          color: var(--text-muted);
          line-height: 1.5;
        }
        .dh-ac-link {
          display: flex;
          align-items: center;
          gap: 6px;
          font-weight: 700;
          font-size: 15px;
          margin-top: 4px;
          color: var(--primary);
        }

        /* Panels: At a Glance + Care Team */
        .dh-panels {
          display: flex;
          gap: 28px;
          flex-wrap: wrap;
        }
        .dh-panel {
          flex: 1;
          min-width: 320px;
        }
        .dh-panel-title {
          font-family: var(--font-display);
          font-size: 20px;
          font-weight: 700;
          color: var(--text);
          margin-bottom: 16px;
        }
        .dh-panel-card {
          background: var(--white);
          border: 1px solid var(--border);
          border-radius: var(--radius);
          overflow: hidden;
          box-shadow: var(--shadow-sm);
        }

        /* At a Glance rows */
        .dh-glance-row {
          padding: 20px;
          border-bottom: 1px solid var(--border);
          display: flex;
          align-items: center;
          gap: 16px;
        }
        .dh-glance-emergency {
          border-left: 4px solid var(--red);
        }
        .dh-glance-clickable {
          cursor: pointer;
          transition: background 0.15s;
        }
        .dh-glance-clickable:hover {
          background: var(--primary-bg);
        }
        .dh-glance-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .dh-glance-info {
          flex: 1;
          min-width: 0;
        }
        .dh-glance-label {
          font-weight: 700;
          font-size: 17px;
          color: var(--text);
        }
        .dh-glance-value {
          font-size: 15px;
          color: var(--text-muted);
          margin-top: 2px;
        }
        .dh-glance-phone {
          font-size: 15px;
          color: var(--red);
          font-weight: 700;
          margin-top: 1px;
        }
        .dh-glance-sub {
          font-size: 14px;
          color: var(--text-muted);
          margin-top: 2px;
        }
        .dh-glance-amber {
          color: var(--amber);
          font-weight: 700;
        }

        /* Care Team rows */
        .dh-care-row {
          padding: 20px;
          border-bottom: 1px solid var(--border);
          display: flex;
          align-items: flex-start;
          gap: 14px;
        }
        .dh-care-avatar {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .dh-care-info {
          flex: 1;
          min-width: 0;
        }
        .dh-care-role {
          font-size: 13px;
          color: var(--text-muted);
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .dh-care-name {
          font-weight: 700;
          font-size: 17px;
          color: var(--text);
          margin-top: 2px;
        }
        .dh-care-detail {
          font-size: 14px;
          color: var(--text-muted);
          margin-top: 2px;
        }
        .dh-care-contacts {
          display: flex;
          gap: 16px;
          margin-top: 4px;
          flex-wrap: wrap;
        }
        .dh-care-contact {
          font-size: 14px;
        }
        .dh-care-contact-label {
          color: var(--text-muted);
        }
        .dh-care-contact-value {
          color: var(--primary);
          font-weight: 700;
        }
        .dh-care-contact-fax {
          color: var(--text);
          font-weight: 600;
        }

        /* Panel footer */
        .dh-panel-footer {
          padding: 14px 20px;
          background: var(--bg);
          text-align: center;
        }
        .dh-panel-link {
          background: none;
          border: none;
          cursor: pointer;
          color: var(--primary);
          font-weight: 700;
          font-size: 14px;
          font-family: var(--font-body);
          padding: 6px 0;
        }
        .dh-panel-link:hover {
          text-decoration: underline;
        }

        /* Responsive */
        @media (max-width: 900px) {
          .dh-actions {
            flex-direction: column;
          }
        }
        @media (max-width: 768px) {
          .dh-welcome {
            padding: 28px 24px;
          }
          .dh-welcome h2 {
            font-size: 24px;
          }
          .dh-panels {
            flex-direction: column;
          }
          .dh-panel {
            min-width: 0;
          }
          .dh-action-card {
            padding: 20px;
          }
        }
        @media (max-width: 480px) {
          .dh-welcome {
            padding: 18px 16px;
          }
          .dh-welcome h2 {
            font-size: 20px;
          }
        }
      `}</style>
    </div>
  );
};

export default DashboardHome;
